import { useState } from 'react'
import { useAuthStore } from '@/shared/session'
import { hintId, track } from '@/shared/lib'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Button, Input } from '@/shared/ui'

type View = 'signin' | 'signup' | 'forgot' | 'sent'

interface LoginDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function LoginDialog({ open, onOpenChange }: LoginDialogProps) {
  const signIn = useAuthStore((s) => s.signIn)
  const signUp = useAuthStore((s) => s.signUp)
  const resetPassword = useAuthStore((s) => s.resetPassword)

  const [view, setView] = useState<View>('signin')
  const [sentContext, setSentContext] = useState<'confirm_email' | 'reset'>('reset')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [username, setUsername] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  function reset() {
    setView('signin')
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setUsername('')
    setInviteCode('')
    setErrorMsg(null)
    setLoading(false)
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset()
    onOpenChange(next)
  }

  function switchView(next: View) {
    setErrorMsg(null)
    setView(next)
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault()
    if (loading) { setErrorMsg('Still signing you in…'); return }
    if (!email.trim()) { setErrorMsg('Enter your email address.'); return }
    if (!password) { setErrorMsg('Enter your password.'); return }
    setLoading(true)
    setErrorMsg(null)
    const error = await signIn(email.trim(), password)
    setLoading(false)
    if (error) { setErrorMsg(error); return }
    track('signed-in')
    onOpenChange(false)
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault()
    if (loading) { setErrorMsg('Still creating your account…'); return }
    if (!inviteCode.trim()) { setErrorMsg('Enter your invite code first.'); return }
    if (!email.trim()) { setErrorMsg('Enter an email address.'); return }
    if (!username.trim()) { setErrorMsg('Choose a username.'); return }
    if (!password || !confirmPassword) { setErrorMsg('Fill in both password fields.'); return }
    if (password !== confirmPassword) { setErrorMsg('Passwords do not match'); return }
    if (password.length < 8) { setErrorMsg('Password must be at least 8 characters'); return }
    setLoading(true)
    setErrorMsg(null)
    const result = await signUp(email.trim(), password, username.trim(), inviteCode.trim())
    setLoading(false)
    if (result === 'confirm_email') {
      setSentContext('confirm_email')
      setView('sent')
    } else if (result === null) {
      onOpenChange(false)
    } else {
      setErrorMsg(result)
    }
  }

  async function handleForgot(e: React.FormEvent) {
    e.preventDefault()
    if (loading) { setErrorMsg('Still sending…'); return }
    if (!email.trim()) { setErrorMsg('Enter your email address.'); return }
    setLoading(true)
    setErrorMsg(null)
    const error = await resetPassword(email.trim())
    setLoading(false)
    if (error) { setErrorMsg(error); return }
    setSentContext('reset')
    setView('sent')
  }

  const titles: Record<View, string> = {
    signin: 'Sign in',
    signup: 'Create account',
    forgot: 'Reset password',
    sent: 'Check your inbox',
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titles[view]}</DialogTitle>
          {view === 'signin' && (
            <DialogDescription>Sign in to sync your characters across devices.</DialogDescription>
          )}
          {view === 'signup' && (
            <DialogDescription>Create an account to save your characters to the cloud.</DialogDescription>
          )}
          {view === 'forgot' && (
            <DialogDescription>Enter your email and we'll send you a reset link.</DialogDescription>
          )}
        </DialogHeader>

        {view === 'sent' && (
          <>
            <p className="text-sm">
              {sentContext === 'confirm_email'
                ? <>A confirmation link has been sent to <strong>{email}</strong>. Click it to activate your account.</>
                : <>A password reset link has been sent to <strong>{email}</strong>.</>
              }
            </p>
            <p className="text-xs text-muted-foreground">Check your spam folder if it doesn't arrive.</p>
            <DialogFooter showCloseButton />
          </>
        )}

        {/* Every form here is noValidate: the browser's own validation bubble
            would pre-empt our handler, refusing empty fields in the browser's
            words instead of the app's. `required` stays for its
            assistive-tech semantics. */}
        {view === 'signin' && (
          <form noValidate onSubmit={handleSignIn} className="flex flex-col gap-3">
            <Field id="si-email" label="Email">
              <Input id="si-email" type="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                required autoFocus disabled={loading} />
            </Field>
            <Field id="si-password" label="Password">
              <Input id="si-password" type="password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                required disabled={loading} />
            </Field>
            <button type="button" className="self-start text-xs text-link hover:text-foreground"
              onClick={() => switchView('forgot')}>
              Forgot password?
            </button>
            {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}
            <DialogFooter>
              <button type="button" className="mr-auto text-xs text-link hover:text-foreground"
                onClick={() => switchView('signup')}>
                Don't have an account? Sign up
              </button>
              <Button type="submit">
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </DialogFooter>
          </form>
        )}

        {view === 'signup' && (
          <form noValidate onSubmit={handleSignUp} className="flex flex-col gap-3">
            <Field id="su-invite" label="Invite code" hint="Ask whoever invited you for one.">
              <Input id="su-invite" type="text" aria-describedby={hintId('su-invite')}
                value={inviteCode} onChange={(e) => setInviteCode(e.target.value)}
                required autoFocus disabled={loading} />
            </Field>
            <Field id="su-username" label="Username">
              <Input id="su-username" type="text"
                value={username} onChange={(e) => setUsername(e.target.value)}
                required disabled={loading} />
            </Field>
            <Field id="su-email" label="Email">
              <Input id="su-email" type="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                required disabled={loading} />
            </Field>
            <Field id="su-password" label="Password" hint="At least 8 characters">
              <Input id="su-password" type="password" aria-describedby={hintId('su-password')}                 value={password} onChange={(e) => setPassword(e.target.value)}
                required disabled={loading} />
            </Field>
            <Field id="su-confirm" label="Confirm password">
              <Input id="su-confirm" type="password"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                required disabled={loading} />
            </Field>
            {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}
            <DialogFooter>
              <button type="button" className="mr-auto text-xs text-link hover:text-foreground"
                onClick={() => switchView('signin')}>
                Already have an account? Sign in
              </button>
              <Button type="submit"
                >
                {loading ? 'Creating account…' : 'Create account'}
              </Button>
            </DialogFooter>
          </form>
        )}

        {view === 'forgot' && (
          <form noValidate onSubmit={handleForgot} className="flex flex-col gap-3">
            <Field id="fp-email" label="Email">
              <Input id="fp-email" type="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                required autoFocus disabled={loading} />
            </Field>
            {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}
            <DialogFooter>
              <button type="button" className="mr-auto text-xs text-link hover:text-foreground"
                onClick={() => switchView('signin')}>
                Back to sign in
              </button>
              <Button type="submit">
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs font-medium">{label}</label>
      {hint && <p id={hintId(id)} className="text-xs text-muted-foreground">{hint}</p>}
      {children}
    </div>
  )
}
